/**
 * NdfTypesFraisPanel — écran 5 du mockup : Types de frais & barème km.
 * Géré par la comptabilité. Reproduit le mockup à l'identique.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { Plus } from 'lucide-react';
import type { NdfTypeFrais, NdfBaremeKm } from '@roronoa-collectivites/shared-types';

const eur = (n: number | null) => (n == null ? '—' : `${n.toFixed(2).replace('.', ',')} €`);

export function NdfTypesFraisPanel() {
  const [types, setTypes] = useState<NdfTypeFrais[]>([]);
  const [bareme, setBareme] = useState<NdfBaremeKm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<NdfTypeFrais | null>(null);
  const [creating, setCreating] = useState(false);

  async function charger() {
    try {
      const [t, b] = await Promise.all([api.notesFrais.types.list(false), api.notesFrais.baremeKm.list()]);
      setTypes(t); setBareme(b);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { void charger(); }, []);

  // Barème présenté en lignes par puissance (3 tranches en colonnes).
  const puissances = [...new Set(bareme.map((b) => `${b.puissanceMin}|${b.puissanceMax ?? ''}`))];
  function labelPuissance(min: number, max: number | null) {
    if (max == null) return `${min} CV et plus`;
    if (min === max) return `${min} CV`;
    return `${min}–${max} CV`;
  }
  function tauxPour(min: number, max: number | null, kmin: number) {
    return bareme.find((b) => b.puissanceMin === min && (b.puissanceMax ?? null) === max && b.trancheKmMin === kmin);
  }

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Types de frais */}
      <Card variant="glass" padded={false}>
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink">Types de frais</h3>
          <Button variant="secondary" size="sm" className="ml-auto" style={{ background: 'oklch(var(--c-compta))', borderColor: 'transparent', color: '#fff' }} onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> Ajouter un type</Button>
        </div>
        <div className="border-b border-border bg-surface/40 px-4 py-2.5 text-xs text-ink-muted">
          Les plafonds correspondent à ce que <strong>votre mairie</strong> rembourse (souvent fixé par délibération). Ils s'affichent au déclarant lors de la saisie. Mode <strong>ferme</strong> = écrêté au plafond ; mode <strong>indicatif</strong> = simple repère, le validateur peut rembourser le réel.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                <th className="px-4 py-2.5 font-medium">Libellé</th>
                <th className="px-4 py-2.5 font-medium">Barème km</th>
                <th className="px-4 py-2.5 font-medium">TVA défaut</th>
                <th className="px-4 py-2.5 text-right font-medium">Plafond</th>
                <th className="px-4 py-2.5 font-medium">Mode plafond</th>
                <th className="px-4 py-2.5 font-medium">Compte M57</th>
                <th className="px-4 py-2.5 font-medium">Actif</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-ink">{t.libelle}</td>
                  <td className="px-4 py-2.5">{t.baremeKm ? <Badge variant="rh">Barème km</Badge> : <span className="text-ink-subtle">—</span>}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{t.tauxTvaDefaut != null ? `${t.tauxTvaDefaut} %` : <span className="text-ink-subtle">—</span>}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-muted">{eur(t.plafond)}</td>
                  <td className="px-4 py-2.5">{t.plafond == null ? <span className="text-ink-subtle">—</span> : t.modePlafond === 'ferme' ? <Badge variant="urgent">Ferme</Badge> : <Badge variant="neutral">Indicatif</Badge>}</td>
                  <td className="px-4 py-2.5 text-ink-subtle">{t.compteM57 ?? '—'}</td>
                  <td className="px-4 py-2.5">{t.actif ? <Badge variant="success">Actif</Badge> : <Badge variant="neutral">Inactif</Badge>}</td>
                  <td className="px-4 py-2.5 text-right"><Button variant="ghost" size="sm" onClick={() => setEdit(t)}>Modifier</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Barème km */}
      <Card variant="glass" padded={false}>
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink">Barème kilométrique (voiture perso)</h3>
          <span className="ml-auto text-xs text-ink-subtle">Révisable chaque année</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                <th className="px-4 py-2.5 font-medium">Puissance fiscale</th>
                <th className="px-4 py-2.5 font-medium">Jusqu'à 5 000 km</th>
                <th className="px-4 py-2.5 font-medium">5 001 – 20 000 km</th>
                <th className="px-4 py-2.5 font-medium">Au-delà</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {puissances.map((p) => {
                const [minS, maxS] = p.split('|');
                const min = Number(minS); const max = maxS === '' ? null : Number(maxS);
                const t1 = tauxPour(min, max, 0); const t2 = tauxPour(min, max, 5001); const t3 = tauxPour(min, max, 20001);
                return (
                  <tr key={p} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5 text-ink">{labelPuissance(min, max)}</td>
                    <td className="px-4 py-2.5 text-ink-muted">{t1 ? `${t1.taux.toFixed(3).replace('.', ',')} €/km` : '—'}</td>
                    <td className="px-4 py-2.5 text-ink-muted">{t2 ? `${t2.taux.toFixed(3).replace('.', ',')} €/km` : '—'}</td>
                    <td className="px-4 py-2.5 text-ink-muted">{t3 ? `${t3.taux.toFixed(3).replace('.', ',')} €/km` : '—'}</td>
                    <td className="px-4 py-2.5 text-right"><BaremeEditButton lignes={[t1, t2, t3]} onSaved={charger} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-border px-4 py-2.5 text-xs text-ink-muted">
          Valeurs indicatives (barème fiscal). À confirmer / ajuster chaque année par la comptabilité. Le calcul dans les notes utilise ces taux.
        </div>
      </Card>

      {(edit || creating) && (
        <TypeFraisModal type={edit} onClose={() => { setEdit(null); setCreating(false); }} onSaved={() => { setEdit(null); setCreating(false); void charger(); }} />
      )}
    </div>
  );
}

function BaremeEditButton({ lignes, onSaved }: { lignes: (NdfBaremeKm | undefined)[]; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const valid = lignes.filter(Boolean) as NdfBaremeKm[];
  const [taux, setTaux] = useState<Record<string, string>>({});
  useEffect(() => { if (open) setTaux(Object.fromEntries(valid.map((l) => [l.id, String(l.taux)]))); }, [open]);
  async function save() {
    for (const l of valid) {
      const v = Number((taux[l.id] ?? '').replace(',', '.'));
      if (!Number.isNaN(v) && v !== l.taux) await api.notesFrais.baremeKm.updateTaux(l.id, v);
    }
    setOpen(false); onSaved();
  }
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>Modifier</Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Modifier le barème"
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button><Button variant="secondary" style={{ background: 'oklch(var(--c-compta))', borderColor: 'transparent', color: '#fff' }} onClick={() => void save()}>Enregistrer</Button></>}>
        <div className="space-y-3">
          {valid.map((l) => (
            <div key={l.id} className="flex items-center gap-3">
              <span className="w-40 text-sm text-ink-muted">{l.trancheKmMin === 0 ? 'Jusqu\'à 5 000 km' : l.trancheKmMax == null ? 'Au-delà de 20 000 km' : '5 001 – 20 000 km'}</span>
              <input type="text" value={taux[l.id] ?? ''} onChange={(e) => setTaux((s) => ({ ...s, [l.id]: e.target.value }))} className="h-10 w-28 rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink" />
              <span className="text-sm text-ink-subtle">€/km</span>
            </div>
          ))}
        </div>
      </Dialog>
    </>
  );
}

function TypeFraisModal({ type, onClose, onSaved }: { type: NdfTypeFrais | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    libelle: type?.libelle ?? '', baremeKm: type?.baremeKm ?? false,
    tauxTvaDefaut: type?.tauxTvaDefaut != null ? String(type.tauxTvaDefaut) : '',
    plafond: type?.plafond != null ? String(type.plafond) : '',
    modePlafond: type?.modePlafond ?? 'indicatif' as 'ferme' | 'indicatif',
    compteM57: type?.compteM57 ?? '', actif: type?.actif ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setSaving(true); setError(null);
    const body = {
      libelle: f.libelle.trim(), baremeKm: f.baremeKm,
      tauxTvaDefaut: f.tauxTvaDefaut ? Number(f.tauxTvaDefaut.replace(',', '.')) : null,
      plafond: f.plafond ? Number(f.plafond.replace(',', '.')) : null,
      modePlafond: f.modePlafond, compteM57: f.compteM57.trim() || null, actif: f.actif,
    };
    try {
      if (type) await api.notesFrais.types.update(type.id, body);
      else await api.notesFrais.types.create(body);
      onSaved();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }
  const inp = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink';
  return (
    <Dialog open onClose={onClose} title={type ? 'Modifier le type de frais' : 'Ajouter un type de frais'}
      footer={<><Button variant="ghost" onClick={onClose}>Annuler</Button><Button variant="secondary" loading={saving} disabled={!f.libelle.trim()} style={{ background: 'oklch(var(--c-compta))', borderColor: 'transparent', color: '#fff' }} onClick={() => void save()}>Enregistrer</Button></>}>
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <div><div className="mb-1 text-xs text-ink-subtle">Libellé</div><input type="text" value={f.libelle} onChange={(e) => setF((s) => ({ ...s, libelle: e.target.value }))} className={inp} /></div>
        <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={f.baremeKm} onChange={(e) => setF((s) => ({ ...s, baremeKm: e.target.checked }))} /> Ce type utilise le barème kilométrique</label>
        {!f.baremeKm && (
          <div className="grid grid-cols-2 gap-3">
            <div><div className="mb-1 text-xs text-ink-subtle">TVA défaut (%)</div><input type="text" value={f.tauxTvaDefaut} onChange={(e) => setF((s) => ({ ...s, tauxTvaDefaut: e.target.value }))} className={inp} /></div>
            <div><div className="mb-1 text-xs text-ink-subtle">Plafond (€)</div><input type="text" value={f.plafond} onChange={(e) => setF((s) => ({ ...s, plafond: e.target.value }))} className={inp} /></div>
          </div>
        )}
        {!f.baremeKm && f.plafond && (
          <div><div className="mb-1 text-xs text-ink-subtle">Mode de plafond</div>
            <div className="inline-flex rounded-r-md border border-border-hi bg-surface/60 p-0.5">
              <button type="button" onClick={() => setF((s) => ({ ...s, modePlafond: 'ferme' }))} className={`rounded-r-sm px-3 py-1.5 text-xs ${f.modePlafond === 'ferme' ? 'bg-danger text-white' : 'text-ink-muted'}`}>Ferme (écrêté)</button>
              <button type="button" onClick={() => setF((s) => ({ ...s, modePlafond: 'indicatif' }))} className={`rounded-r-sm px-3 py-1.5 text-xs ${f.modePlafond === 'indicatif' ? 'bg-ink text-surface' : 'text-ink-muted'}`}>Indicatif (repère)</button>
            </div>
          </div>
        )}
        <div><div className="mb-1 text-xs text-ink-subtle">Compte M57</div><input type="text" value={f.compteM57} onChange={(e) => setF((s) => ({ ...s, compteM57: e.target.value }))} className={inp} /></div>
        <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={f.actif} onChange={(e) => setF((s) => ({ ...s, actif: e.target.checked }))} /> Actif</label>
      </div>
    </Dialog>
  );
}
